import React, { useEffect, useState } from 'react'
import { useLanguage } from '../../../components/context/LanguageContext'
import { Button, Card, Col, Row, Statistic, Tag, Typography, List, Collapse, Spin, message, Modal } from 'antd'
import {
  PlayCircleOutlined,
  StopOutlined,
  FolderOpenOutlined,
  FileTextOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import { apiRequest } from '../../../helpers'

const { Title, Paragraph, Text } = Typography
const { Panel } = Collapse

const LicitatiiStatusTab = ({ agent, onStartProcessing, onStopProcessing, processing, refreshData }) => {
  const languageContext = useLanguage()
  const [processingData, setProcessingData] = useState(null)
  const [detailedData, setDetailedData] = useState(null)
  const [loadingDetailedData, setLoadingDetailedData] = useState(false)

  useEffect(() => {
    if (agent.processingData) {
      try {
        setProcessingData(JSON.parse(agent.processingData))
      } catch (error) {
        console.error('Error parsing processing data:', error)
        setProcessingData(null)
      }
    } else {
      setProcessingData(null)
    }
  }, [agent.processingData])

  const fetchDetailedProcessingData = async () => {
    if (!agent.id) return

    setLoadingDetailedData(true)
    try {
      const response = await apiRequest(`${process.env.REACT_APP_API}agents/licitatii/${agent.id}/processing-data`, 'GET')
      if (response.statusCode === 200) {
        setDetailedData(response.data)
      }
    } catch (error) {
      console.error('Error fetching detailed processing data:', error)
      message.error(languageContext.t('Failed to load detailed processing data'))
    } finally {
      setLoadingDetailedData(false)
    }
  }

  const resetFolderStatus = async folderName => {
    try {
      const response = await apiRequest(`${process.env.REACT_APP_API}agents/licitatii/${agent.id}/reset-folder`, 'POST', { folderName: folderName })

      if (response.statusCode === 200) {
        message.success(languageContext.t('Folder status reset successfully. It will be reprocessed on next run.'))
        // Refresh the data to show updated status
        refreshData()
        fetchDetailedProcessingData()
      }
    } catch (error) {
      console.error('Error resetting folder status:', error)
      message.error(languageContext.t('Failed to reset folder status'))
    }
  }

  const confirmResetFolder = folderName => {
    Modal.confirm({
      title: languageContext.t('Reset Folder Status'),
      content: languageContext.t(
        `Are you sure you want to reset the processing status for folder "${folderName}"? This will mark it for reprocessing on the next run.`
      ),
      onOk: () => resetFolderStatus(folderName),
      okText: languageContext.t('Yes, Reset'),
      cancelText: languageContext.t('Cancel'),
    })
  }

  const transferProcessedData = async () => {
    try {
      const response = await apiRequest(`${process.env.REACT_APP_API}agents/licitatii/${agent.id}/transfer-processed-data`, 'POST', {})

      if (response.statusCode === 200) {
        const { transferredCount, skippedCount, totalProcessed } = response.data
        message.success(
          languageContext.t(`Transfer completed: ${transferredCount} licitatii updated, ${skippedCount} skipped from ${totalProcessed} total`)
        )
        // Refresh the data
        refreshData()
      }
    } catch (error) {
      console.error('Error transferring processed data:', error)
      message.error(languageContext.t('Failed to transfer processed data'))
    }
  }

  const confirmTransferProcessedData = () => {
    Modal.confirm({
      title: languageContext.t('Transfer Processed Data'),
      content: languageContext.t(
        'This will transfer AI processed responses from the agent to the corresponding licitatii records. Only licitatii without existing AI processed data will be updated. Continue?'
      ),
      onOk: () => transferProcessedData(),
      okText: languageContext.t('Yes, Transfer'),
      cancelText: languageContext.t('Cancel'),
    })
  }

  useEffect(() => {
    if (agent.status === 'completed' && processingData) {
      fetchDetailedProcessingData()
    }
  }, [agent.status, processingData])

  const getStatusColor = status => {
    switch (status) {
      case 'processing':
        return 'blue'
      case 'completed':
        return 'green'
      case 'error':
        return 'red'
      case 'stopped':
      default:
        return 'default'
    }
  }

  const getStatusText = status => {
    switch (status) {
      case 'processing':
        return languageContext.t('Processing')
      case 'completed':
        return languageContext.t('Completed')
      case 'error':
        return languageContext.t('Error')
      case 'stopped':
      default:
        return languageContext.t('Stopped')
    }
  }

  const exportQuestionsToCSV = (folderName, questions) => {
    if (!questions || questions.length === 0) {
      message.warning(languageContext.t('No questions to export'))
      return
    }

    // Helper function to escape CSV fields
    const escapeCsvField = field => {
      if (!field) return '""'
      const stringField = String(field)
      // If field contains comma, newline, or quote, wrap in quotes and escape internal quotes
      if (stringField.includes(',') || stringField.includes('\n') || stringField.includes('"')) {
        return `"${stringField.replace(/"/g, '""')}"`
      }
      return stringField
    }

    // Create CSV content with UTF-8 BOM for proper Excel compatibility
    const BOM = '\uFEFF'
    const headers = [languageContext.t('Question'), languageContext.t('Answer')]
    const csvRows = [headers.map(escapeCsvField).join(',')]

    questions.forEach(qa => {
      const question = qa.question || ''
      const answer = qa.answer || ''
      csvRows.push([escapeCsvField(question), escapeCsvField(answer)].join(','))
    })

    const csvContent = BOM + csvRows.join('\n')

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${folderName}_Questions_Answers_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    message.success(languageContext.t('Questions and answers exported successfully'))
  }

  const renderOpenAIResponses = folderResult => {
    if (!folderResult.results || !Array.isArray(folderResult.results)) {
      return null
    }

    return folderResult.results.map((result, idx) => {
      if (result.type === 'folder_analysis' && result.analysis && result.analysis.questions) {
        return (
          <div key={idx} style={{ marginTop: '16px' }}>
            <Title level={5} style={{ marginBottom: '12px' }}>
              <MessageOutlined /> {languageContext.t('OpenAI Analysis Results')}
            </Title>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>{languageContext.t('Analyzed Files')}: </Text>
              <Text>
                {result.files
                  ?.filter(f => !f.error)
                  .map(f => f.name)
                  .join(', ') || 'N/A'}
              </Text>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>{languageContext.t('Analysis Date')}: </Text>
              <Text>{result.analysis.analyzedAt ? new Date(result.analysis.analyzedAt).toLocaleString() : 'N/A'}</Text>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>{languageContext.t('Thread ID')}: </Text>
              <Text code>{result.analysis.threadId || 'N/A'}</Text>
            </div>

            <Collapse ghost>
              <Panel
                header={
                  <div>
                    <QuestionCircleOutlined />
                    <span style={{ marginLeft: '8px' }}>
                      {languageContext.t('Questions & Answers')} ({result.analysis.questions?.length || 0})
                    </span>
                  </div>
                }
                key="questions"
                extra={
                  <Button
                    type="primary"
                    size="small"
                    onClick={e => {
                      e.stopPropagation()
                      exportQuestionsToCSV(folderResult.folder, result.analysis.questions)
                    }}
                    style={{ marginRight: '8px' }}
                  >
                    {languageContext.t('Export CSV')}
                  </Button>
                }
              >
                <List
                  dataSource={result.analysis.questions || []}
                  renderItem={(qa, qaIdx) => (
                    <List.Item key={qaIdx}>
                      <div style={{ width: '100%' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <Text strong style={{ color: '#1890ff' }}>
                            <QuestionCircleOutlined /> {languageContext.t('Question')}:
                          </Text>
                          <div
                            style={{
                              marginTop: '4px',
                              padding: '8px',
                              backgroundColor: '#f0f8ff',
                              borderRadius: '4px',
                              borderLeft: '3px solid #1890ff',
                            }}
                          >
                            {qa.question}
                          </div>
                        </div>
                        <div>
                          <Text strong style={{ color: qa.status === 'completed' ? '#52c41a' : '#ff4d4f' }}>
                            <MessageOutlined /> {languageContext.t('Answer')}:
                          </Text>
                          <Tag color={qa.status === 'completed' ? 'green' : 'red'} style={{ marginLeft: '8px' }}>
                            {qa.status === 'completed' ? languageContext.t('Completed') : languageContext.t('Error')}
                          </Tag>
                          <div
                            style={{
                              marginTop: '4px',
                              padding: '12px',
                              backgroundColor: qa.status === 'completed' ? '#f6ffed' : '#fff2f0',
                              borderRadius: '4px',
                              borderLeft: `3px solid ${qa.status === 'completed' ? '#52c41a' : '#ff4d4f'}`,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {qa.answer}
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </Panel>
            </Collapse>
          </div>
        )
      }
      return null
    })
  }

  const renderProcessingResults = () => {
    if (!processingData || !Array.isArray(processingData)) {
      return null
    }

    const totalFolders = processingData.length
    const completedFolders = processingData.filter(item => !item.error).length
    const errorFolders = processingData.filter(item => item.error).length

    return (
      <Card
        title={
          <div>
            {languageContext.t('Processing Results')}
            {agent.status === 'completed' && (
              <Button type="link" size="small" onClick={fetchDetailedProcessingData} loading={loadingDetailedData} style={{ marginLeft: '8px' }}>
                {languageContext.t('Refresh Details')}
              </Button>
            )}
          </div>
        }
        className="mt-3"
      >
        <Row gutter={16} className="mb-3">
          <Col span={8}>
            <Statistic title={languageContext.t('Total Folders')} value={totalFolders} prefix={<FolderOpenOutlined />} />
          </Col>
          <Col span={8}>
            <Statistic title={languageContext.t('Processed Successfully')} value={completedFolders} valueStyle={{ color: '#3f8600' }} />
          </Col>
          <Col span={8}>
            <Statistic title={languageContext.t('Errors')} value={errorFolders} valueStyle={{ color: '#cf1322' }} />
          </Col>
        </Row>

        {loadingDetailedData && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '8px' }}>{languageContext.t('Loading detailed processing data...')}</div>
          </div>
        )}

        <List
          itemLayout="vertical"
          dataSource={detailedData?.processingData || processingData}
          renderItem={(item, index) => (
            <List.Item
              key={index}
              actions={[
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => confirmResetFolder(item.folder)}
                  title={languageContext.t('Reset folder status for reprocessing')}
                >
                  {languageContext.t('Reset')}
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={<FolderOpenOutlined />}
                title={
                  <div>
                    {item.folder}
                    {item.error ? (
                      <Tag color="red" className="ml-2">
                        Error
                      </Tag>
                    ) : (
                      <Tag color="green" className="ml-2">
                        Success
                      </Tag>
                    )}
                  </div>
                }
                description={
                  item.error ? (
                    <span style={{ color: '#cf1322' }}>{item.error}</span>
                  ) : (
                    <div>
                      <div>
                        <FileTextOutlined /> {languageContext.t('Total Files')}: {item.totalFiles || 0}
                      </div>
                      <div>
                        <FileTextOutlined /> {languageContext.t('Processed Files')}: {item.processedFiles || 0}
                      </div>
                    </div>
                  )
                }
              />
              {item.results && !item.error && (
                <div className="ml-4">
                  <List
                    size="small"
                    dataSource={item.results}
                    renderItem={(result, idx) => (
                      <List.Item key={idx}>
                        <div>
                          <strong>{result.file || result.folder}</strong>
                          {result.error ? (
                            <Tag color="red" className="ml-2">
                              Error: {result.error}
                            </Tag>
                          ) : (
                            <Tag color="green" className="ml-2">
                              Processed
                            </Tag>
                          )}
                        </div>
                      </List.Item>
                    )}
                  />
                  {renderOpenAIResponses(item)}
                </div>
              )}
            </List.Item>
          )}
        />
      </Card>
    )
  }

  return (
    <div>
      <Card title={languageContext.t('Agent Status')}>
        <Row gutter={16} className="mb-3">
          <Col span={12}>
            <Card>
              <Statistic
                title={languageContext.t('Current Status')}
                value={getStatusText(agent.status)}
                valueRender={() => (
                  <Tag color={getStatusColor(agent.status)} style={{ fontSize: '16px', padding: '4px 12px' }}>
                    {getStatusText(agent.status)}
                  </Tag>
                )}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card>
              <Statistic
                title={languageContext.t('Agent Status')}
                value={agent.active ? languageContext.t('Active') : languageContext.t('Inactive')}
                valueRender={() => (
                  <Tag color={agent.active ? 'green' : 'red'} style={{ fontSize: '16px', padding: '4px 12px' }}>
                    {agent.active ? languageContext.t('Active') : languageContext.t('Inactive')}
                  </Tag>
                )}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={24}>
            <Title level={5}>{languageContext.t('Controls')}</Title>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={onStartProcessing}
                disabled={processing || agent.status === 'processing' || !agent.active}
                loading={processing}
                className="dyn-btn-primary"
              >
                {languageContext.t('Start Processing')}
              </Button>
              <Button
                icon={<StopOutlined />}
                onClick={onStopProcessing}
                disabled={processing || agent.status !== 'processing'}
                loading={processing}
                className="dyn-btn-secondary"
              >
                {languageContext.t('Stop Processing')}
              </Button>
              {agent.processCentralizatorDocuments && processingData && processingData.length > 0 && (
                <Button
                  icon={<SwapOutlined />}
                  onClick={confirmTransferProcessedData}
                  disabled={processing || agent.status === 'processing'}
                  className="dyn-btn-warning"
                  title={languageContext.t('Transfer AI processed responses to licitatii records')}
                >
                  {languageContext.t('Transfer AI Data')}
                </Button>
              )}
            </div>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Title level={5}>{languageContext.t('Configuration')}</Title>
            <div>
              <Paragraph>
                <strong>{languageContext.t('Storage ID')}:</strong> {agent.storageId || languageContext.t('Not configured')}
              </Paragraph>
              <Paragraph>
                <strong>{languageContext.t('AI Connector ID')}:</strong> {agent.aiId || languageContext.t('Not configured')}
              </Paragraph>
              <Paragraph>
                <strong>{languageContext.t('Questions')}:</strong>
              </Paragraph>
              <Collapse defaultActiveKey={[]} style={{ background: '#f5f5f5', borderRadius: '4px' }}>
                <Collapse.Panel header={languageContext.t('Show Questions')} key="1">
                  <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                    {agent.questions || languageContext.t('No questions configured')}
                  </Paragraph>
                </Collapse.Panel>
              </Collapse>
            </div>
          </Col>
        </Row>
      </Card>

      {renderProcessingResults()}
    </div>
  )
}

export default LicitatiiStatusTab
